"use client";

import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, LABEL, WIDGET_H3 } from "@/components/tool-kit/styles";
import { MacroLineFigure } from "@/components/tools/econ-macro/line-figure";
import { useMoney } from "@/components/tools/finance/shared";
import type { Strings } from "@/components/tools/inflation-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  adjustForInflation,
  averageAnnualInflation,
  cpiOf,
  cumulativeInflation,
  yearRows,
} from "@/lib/tool-kit/econ-macro/inflation";

const clean = (value: number) => (value === 0 ? 0 : value);

export function InflationResults({
  amount,
  from,
  to,
  strings,
}: {
  amount: number;
  from: number;
  to: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney("USD");
  const year = (value: number) => fmt(value, { useGrouping: false });
  const pct = (value: number) =>
    `${fmt(clean(value) * 100, { maximumFractionDigits: 2 })}%`;
  const cpi = (value: number) => fmt(value, { maximumFractionDigits: 3 });
  const value = adjustForInflation(amount, from, to);
  const cumulative = cumulativeInflation(from, to);
  const annual = averageAnnualInflation(from, to);
  const cpiFrom = cpiOf(from);
  const cpiTo = cpiOf(to);
  const rows = yearRows(amount, from, to);
  const first = rows[0];
  const last = rows.at(-1);
  const labels = { from: year(from), to: year(to) };
  const values = {
    ...labels,
    cpiFrom: cpi(cpiFrom),
    cpiTo: cpi(cpiTo),
    amount: money(amount),
    value: money(value),
    cumulative: pct(cumulative),
    annual: annual === null ? strings.notAvailable : pct(annual),
    years: fmt(Math.abs(to - from)),
    power: fmt(cpiFrom / cpiTo, { maximumFractionDigits: 4 }),
  };
  const line = "font-mono text-muted-foreground text-sm leading-relaxed";
  return (
    <div className="space-y-5">
      <p className="text-foreground">
        {t(strings.summary, {
          amount: values.amount,
          from: values.from,
          to: values.to,
          value: values.value,
        })}
      </p>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.valueCard, labels)}
          value={values.value}
        />
        <StatCard
          label={t(strings.cumulativeCard, labels)}
          value={values.cumulative}
        />
        <StatCard label={strings.annualCard} value={values.annual} />
        <StatCard label={strings.powerCard} value={values.power} />
      </StatGrid>
      <div className={`${CARD} space-y-1`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className={line}>{t(strings.workCpi, values)}</p>
        <p className={line}>{t(strings.workValue, values)}</p>
        <p className={line}>{t(strings.workCumulative, values)}</p>
        {annual === null ? null : (
          <p className={line}>{t(strings.workAnnual, values)}</p>
        )}
        <p className={line}>{t(strings.workPower, values)}</p>
      </div>
      {rows.length > 1 && first && last ? (
        <>
          <MacroLineFigure
            description={t(strings.chartDescription, {
              amount: values.amount,
              from: values.from,
              first: year(first.year),
              last: year(last.year),
              value: money(last.value),
            })}
            fallbackName="inflation"
            fmt={fmt}
            id="inf-chart"
            labels={strings}
            names={[strings.seriesValue]}
            numeric
            rows={rows.map((row) => ({
              label: year(row.year),
              x: row.year,
              values: [row.value],
            }))}
            showTable={false}
            title={strings.chartTitle}
            xTitle={strings.chartXTitle}
            yTitle={strings.chartYTitle}
          />
          <details>
            <summary className={`${LABEL} cursor-pointer`}>
              {strings.tableSummary}
            </summary>
            <div className="mt-3">
              <DataTable
                head={[
                  strings.yearColumn,
                  strings.cpiColumn,
                  strings.inflationColumn,
                  strings.valueColumn,
                ]}
                rows={rows.map((row) => [
                  year(row.year),
                  cpi(row.cpi),
                  row.inflation === null
                    ? strings.notAvailable
                    : pct(row.inflation),
                  money(row.value),
                ])}
              />
            </div>
          </details>
        </>
      ) : null}
      <Notice>{strings.limits}</Notice>
    </div>
  );
}
